import { FC, ReactElement, Suspense, lazy, useEffect, useMemo } from 'react';
import { Route, Routes, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useWeb3React } from '@web3-react/core';

// Components
import WrongNetwork from 'Components/WrongNetwork';
import UnsupportedWallet from 'Components/UnsupportedWallet';
import Loader from 'Components/ds/Loader';
import SecuritizeRedirect from 'Components/SecuritizeRedirect';
import ProtectedRoute from 'Components/ProtectedRoute';

// Hooks
import useIsGnosisSafe from 'Hooks/useIsGnosisSafe';
import useAuth from 'Hooks/useAuth';

// Routers
const BorrowV2 = lazy(() => import('./BorrowV2'));
const DelegateV2 = lazy(() => import('./DelegateV2'));
const Lend = lazy(() => import('./Lend'));
const XMPL = lazy(() => import('./XMPL'));

// Pages
import { PoolsV2Transition } from 'Pages/PoolsV2Transition';
import SignupLogin from 'Pages/SignupLogin/SignupLogin';

// Constants
import { CHAIN_ID, REDIRECT_PATHS } from 'Constants';

const Router: FC = (): ReactElement => {
  const { isAuthenticated, loading } = useAuth();
  const { isActive, chainId } = useWeb3React();
  const gnosisConnectionType = useIsGnosisSafe();
  const navigate = useNavigate();
  const { pathname, hash } = useLocation();

  const isUnsupportedChainIdSelected = useMemo(() => {
    if (!isActive || !chainId) return false;

    return chainId !== CHAIN_ID;
  }, [chainId, isActive]);

  useEffect(() => {
    if (isUnsupportedChainIdSelected) {
      navigate('/wrong-network');
    } else if (gnosisConnectionType === 'gnosis-wallet-connect') {
      navigate('/unsupported-wallet');
    } else if (
      (!isUnsupportedChainIdSelected && pathname === '/wrong-network') ||
      (['none', 'gnosis-default'].includes(gnosisConnectionType) && pathname === '/unsupported-wallet')
    ) {
      navigate('/');
    } else if (isOldV1Route(pathname)) {
      const [, path] = pathname.split('/');
      let redirectPath = path;
      if (redirectPath === 'earn') redirectPath = 'lend';
      navigate(`/v2/${redirectPath}`);
    } else if (shouldRedirect(pathname)) {
      const redirectDestination = REDIRECT_PATHS[pathname];
      navigate(redirectDestination);
    }
  }, [isUnsupportedChainIdSelected, gnosisConnectionType, history, pathname]);

  // Scroll to top of the page when user navigates to a new page
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  // Scroll to component based on url hash
  useEffect(() => {
    if (hash.length) {
      const id = hash.replace('#', '');
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView();
      }
    }
  }, [hash]);

  const isOldV1Route = (pathname: string) => {
    const [, path] = pathname.split('/');

    return ['earn', 'borrow', 'delegate'].includes(path);
  };

  const shouldRedirect = (pathname: string): boolean => {
    const pathsToRedirect = Object.keys(REDIRECT_PATHS);

    return pathsToRedirect.includes(pathname.toLowerCase());
  };

  return (
    <Routes>
      <Route path='/' element={<Navigate to='/v2/lend/dashboard' replace />} />

      <Route
        path='/xmpl/*'
        element={
          <Suspense fallback={<Loader />}>
            <XMPL />
          </Suspense>
        }
      />

      {/* Pools V2 routes */}
      <Route
        path='/v2/delegate/*'
        element={
          <ProtectedRoute isAllowed={isAuthenticated} loading={loading} redirectPath={'/signup'}>
            <Suspense fallback={<Loader />}>
              <DelegateV2 />
            </Suspense>
          </ProtectedRoute>
        }
      />

      <Route
        path='/v2/lend/*'
        element={
          <ProtectedRoute isAllowed={isAuthenticated} loading={loading} redirectPath={'/signup'}>
            <Suspense fallback={<Loader />}>
              <Lend />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path='/v2/borrow/*'
        element={
          <ProtectedRoute isAllowed={isAuthenticated} loading={loading} redirectPath={'/signup'}>
            <Suspense fallback={<Loader />}>
              <BorrowV2 />
            </Suspense>
          </ProtectedRoute>
        }
      />

      {/* Log in / Sign up routes */}
      <Route path='/signup' element={<SignupLogin formType='signup' />} />
      <Route path='/login' element={<SignupLogin formType='login' />} />

      {/* Other routes */}
      <Route
        path='/v2/maple-upgrade'
        element={
          <Suspense fallback={<Loader />}>
            <PoolsV2Transition />
          </Suspense>
        }
      />
      <Route
        path='/wrong-network'
        element={
          <Suspense fallback={<Loader />}>
            <WrongNetwork />
          </Suspense>
        }
      />
      <Route
        path='/unsupported-wallet'
        element={
          <Suspense fallback={<Loader />}>
            <UnsupportedWallet />
          </Suspense>
        }
      />

      {/* Securitize */}
      <Route
        path='/securitize'
        element={
          <Suspense fallback={<Loader />}>
            <SecuritizeRedirect />
          </Suspense>
        }
      />

      <Route path='*' element={<Navigate to='/v2/lend/dashboard' replace />} />
    </Routes>
  );
};

export default Router;
